import {useRef} from 'react';
import {
    Alert,
    Anchor,
    Button,
    Group,
    SimpleGrid,
    Stack,
    Text,
} from '@mantine/core';
import {
    NavLink,
    useNavigate,
} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useZones} from '../hooks/useZones';
import {useZonesSearch} from '../hooks/useZonesSearch';
import {useZonesSelection} from '../hooks/useZonesSelection';
import {useZonesFilter} from '../hooks/useZonesQuery';
import {useZonesTableColumns} from '../hooks/useZonesTableColumns';
import {useZoneCreateConnections} from '../hooks/useZoneCreateConnections';
import {ConnectionFailuresAlert} from '../components/ConnectionFailuresAlert';
import {DnsZone} from '../types';
import config from '../config';
import providerConnectionsConfig from '../../provider-connections/config';

// ⚠️ NAMED ZonesTable, NOT RecordsTable.
//
// The hosted-zones module's `RecordsTable.tsx` renders ZONES, not records — a
// documented naming decoy that FNP-1194 exists to rename. This module has a
// sibling records concept (`/zones/:zoneId/records`), so reproducing that name
// here would make "RecordsTable" ambiguous between two real things in the same
// feature area rather than merely misleading in one.
export const ZonesTable = () => {

    const {columns} = useZonesTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        failures,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchRecords,
        reload,
        isReloading,
    } = useZones();

    // Same module-scoped stores the list hook filters on (FNP-815) — this
    // component owns the search input, the hook owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useZonesSearch();

    const {filters} = useZonesFilter();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // FNP-1191 CRUD half — ROW SELECTION, feeding the toolbar's Delete.
    //
    // ⚠️ Module-scoped rather than lifted into IndexPage as the DELETE pattern
    // doc does. The header is rendered by SimplePageLayout as a `pageHeader`
    // prop and this table as its child, so the two are not siblings that can
    // share a useState without threading it through the layout in between.
    // Same substance, same store shape as the keyword and filter stores this
    // module already has — see hooks/useZonesSelection.
    //
    // ⚠️ THERE IS STILL NO ROW EDIT ACTION, and that is not an omission: the
    // neutral schema has no `updateDnsZone` (verified against the deployed TEST
    // API). A zone's mutable surface is its RECORDS, which are a sibling
    // feature with mutations of their own.
    const {
        selectedRecords,
        setSelectedRecords,
    } = useZonesSelection();

    // FNP-1249 — the connection COUNT, read only to tell two empty tables apart.
    //
    // ⚠️ REUSES useZoneCreateConnections RATHER THAN ADDING A SECOND QUERY. The
    // Add button already asks this exact question on this exact page, so a new
    // hook would be a second request for the same rows on the same render, and
    // a second answer that could disagree with the first.
    //
    // `hasAnyConnection` — NOT `connections.length`. The hook's `connections`
    // is narrowed to those that can CREATE zones, which is the Add button's
    // question, not this one. A tenant with one read-only connection has a
    // connection: their providers WERE asked and returned nothing, so the
    // original message is the true one for them.
    const {
        hasAnyConnection,
        loading: connectionsLoading,
        isError: connectionsError,
    } = useZoneCreateConnections();

    // The empty states mean DIFFERENT things and `noRecordsText` is one string,
    // so the distinction is branched here rather than flattened into a single
    // message that sends an operator hunting for a bug that is not there.
    const searching = keyword.trim() !== '';
    const filtering = Object.keys(filters).length > 0;

    // ⚠️ ONLY WHEN THE COUNT IS ACTUALLY KNOWN TO BE ZERO.
    //
    // A loading query and a FAILED one both yield an empty list, and neither is
    // evidence of anything. Claiming "you have no connections" off a rejected
    // request would tell a tenant with three of them that they have none — the
    // same class of falsehood this ticket exists to remove, pointed the other
    // way. Both unknown cases fall through to the original message.
    const noConnectionsAtAll = !connectionsLoading && !connectionsError && !hasAnyConnection;

    let noRecordsText = I18n.get('No zones found on your connected providers');
    if (searching && filtering) {
        noRecordsText = I18n.get('No loaded zone matches this search and filter');
    } else if (searching) {
        noRecordsText = I18n.get('No loaded zone matches this search');
    } else if (filtering) {
        noRecordsText = I18n.get('No loaded zone matches this filter');
    } else if (noConnectionsAtAll) {
        // ⚠️ Ordered ABOVE the failures branch but BELOW search/filter. A tenant
        // with no connections cannot have failures — a failure names a
        // connection that could not be read — so the two never compete. Search
        // and filter DO outrank it: those states describe what the operator just
        // typed, which is the more specific and more actionable answer.
        //
        // The string is still set for the PAGINATION FOOTER, which renders
        // `noRecordsText` and takes no node. The table itself renders the
        // richer `emptyState` below.
        noRecordsText = I18n.get('No DNS providers are connected yet');
    } else if (failures.length > 0) {
        // ⚠️ The empty table plus a failure is NOT "you have no zones". Every
        // connection that could be read returned nothing, and at least one
        // could not be read at all — so the honest answer is that this may not
        // be the whole picture.
        noRecordsText = I18n.get('No zones loaded. See the connection warning above — some connections could not be read.');
    }

    // ⚠️ THE ONE EMPTY STATE WITH SOMEWHERE TO GO, so it is the one rendered as
    // an ELEMENT rather than a sentence.
    //
    // `noRecordsText` is typed `string` by mantine-datatable and cannot hold a
    // link; `emptyState` takes a node and REPLACES the whole empty body when
    // set. So it is set ONLY for this branch — passing it unconditionally would
    // override the other four messages with a blank.
    //
    // An empty state that names a problem and offers no way out is a dead end,
    // and this is the first screen a new tenant sees.
    const emptyState = noConnectionsAtAll
        ? (
            <Stack gap={'xs'} align={'center'} p={'xl'}>
                <Text size={'sm'}>
                    {I18n.get('No DNS providers are connected yet')}
                </Text>
                <Text size={'sm'} c={'dimmed'}>
                    {I18n.get('Zones are read from the providers you connect. Connect one to see your zones here.')}
                </Text>
                {/* NavLink is this app's in-app navigation idiom — an Anchor
                    with a plain href would reload the whole SPA. Anchor
                    supplies only the link styling, NavLink the behaviour. */}
                <Anchor component={NavLink} to={providerConnectionsConfig.basePath} size={'sm'}>
                    {I18n.get('Connect a DNS provider')}
                </Anchor>
            </Stack>
        )
        : undefined;

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {/* ⚠️ THE FAILURE BANNER SITS ABOVE THE TABLE AND THE TABLE STILL
                RENDERS. Partial success is success-with-a-warning: the zones
                that loaded are real and complete for their connections, and
                hiding them behind an error state would be a worse lie than the
                silent undercount this banner exists to prevent. */}
            <ConnectionFailuresAlert
                failures={failures}
                onRetry={reload}
                isRetrying={isReloading}
            />

            {/* Distinct from the banner above: this is the QUERY failing, so
                nothing at all is known — not a partial answer. */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load zones')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your zones. This is not a provider problem — the request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<DnsZone>
                columns={columns}
                records={visibleItems}
                idAccessor={'id'}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                // Client-side sorting, over the pages loaded so far — the same
                // scope the keyword and filters work on. Token pagination
                // cannot reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                emptyState={emptyState}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // The connectionId rides along, because /zones/:zoneId alone
                // cannot address a zone — getDnsZone needs both.
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}?connectionId=${encodeURIComponent(record.connectionId)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one. The provider/delegation filters narrow the same window
                // the keyword does, so either one must select that state.
                filterTerm={searching ? keyword : (filtering ? 'filtered' : undefined)}
                noun={'zone'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
